import { Capacitor } from '@capacitor/core';
import { Box } from '@deckee/deck-hand';
import classNames from 'classnames/bind';
import React from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { captureEvent } from '../../../services/event-capture';
import {
  ROUTE_MAP,
  ROUTE_TRIP_ACTIVE,
  ROUTE_WEATHER_MOST_RECENT_POSITION,
} from '../../../services/route/route-names';
import { transitionToNextRoute } from '../../../services/route/route-navigation';
import { selectShowingMenuBar } from '../../../services/store/modules/app-controls';
import { selectCurrentMember } from '../../../services/store/modules/backend-service';
import { MenuBar, MenuBarItem } from '../../shared/MenuBar/MenuBar';
import MemberAvatar from '../../shared/_deprecated/components/MemberAvatar/MemberAvatar';
import {
  go,
  goFilled,
  itinerary,
  itineraryFilled,
  locationOutlined,
  locationOutlinedFilled,
  weather,
  weatherFilled,
} from '../../shared/vector-images';
import styles from './AppControls.scss';

// eslint-disable-next-line no-shadow
enum MenuItemEnum {
  PLAN = 'plan',
  PREPARE = 'prepare',
  GO = 'go',
  WEATHER = 'weather',
  YOU = 'you',
}

const cx = classNames.bind(styles);

const ConstructMenuBar = (
  activeMenu: string,
  member: { id: any; image: any },
) => {
  const history = useHistory();
  const theme = useTheme();

  const { image } = member || {};

  const handleMenuBarItemClick = (menuName: MenuItemEnum) => () => {
    captureEvent({
      type: 'menu-bar: select item',
      payload: {
        name: menuName,
      },
      sendToAmplitude: true,
    });

    switch (menuName) {
      case MenuItemEnum.YOU:
        history.push('/maps/you');
        break;
      case MenuItemEnum.PREPARE:
        history.push('/maps/prepare');
        break;
      case MenuItemEnum.GO:
        transitionToNextRoute(ROUTE_TRIP_ACTIVE);
        break;
      case MenuItemEnum.WEATHER:
        transitionToNextRoute(ROUTE_WEATHER_MOST_RECENT_POSITION);
        break;
      case MenuItemEnum.PLAN:
        transitionToNextRoute(ROUTE_MAP);
        break;
      default:
    }
  };

  return (
    // Be mindful that if the MenuBarItem active prop is changed then this will affect
    // the way the active menu item is set below
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <MenuBar className={cx('app-controls__menu-bar')}>
      <MenuBarItem
        key={MenuItemEnum.PLAN}
        active={activeMenu === MenuItemEnum.PLAN}
        label="Explore"
        action={handleMenuBarItemClick(MenuItemEnum.PLAN)}
        // Likely to include a filled version, so leave condition for now
        icon={
          activeMenu === MenuItemEnum.PLAN
            ? locationOutlinedFilled()
            : locationOutlined()
        }
      />
      <MenuBarItem
        key={MenuItemEnum.PREPARE}
        active={activeMenu === MenuItemEnum.PREPARE}
        label="Prepare"
        action={handleMenuBarItemClick(MenuItemEnum.PREPARE)}
        // Likely to include a filled version, so leave condition for now
        icon={
          activeMenu === MenuItemEnum.PREPARE ? itineraryFilled() : itinerary()
        }
      />
      {Capacitor.isNativePlatform() && (
        <MenuBarItem
          key={MenuItemEnum.GO}
          active={activeMenu === MenuItemEnum.GO}
          label="Go"
          action={handleMenuBarItemClick(MenuItemEnum.GO)}
          icon={activeMenu === MenuItemEnum.GO ? goFilled() : go()}
        />
      )}
      <MenuBarItem
        key={MenuItemEnum.WEATHER}
        active={activeMenu === MenuItemEnum.WEATHER}
        label="Weather"
        action={handleMenuBarItemClick(MenuItemEnum.WEATHER)}
        icon={activeMenu === MenuItemEnum.WEATHER ? weatherFilled() : weather()}
      />
      <MenuBarItem
        key={MenuItemEnum.YOU}
        active={activeMenu === MenuItemEnum.YOU}
        label="You"
        action={handleMenuBarItemClick(MenuItemEnum.YOU)}
      >
        <Box
          mb="2px"
          borderRadius="50%"
          border="2px solid"
          borderColor={
            activeMenu === MenuItemEnum.YOU
              ? theme.colors.primary
              : 'transparent'
          }
        >
          <MemberAvatar
            memberAvatarId={image}
            width={24}
            height={24}
            addBorder={false}
            applyElevation={false}
          />
        </Box>
      </MenuBarItem>
    </MenuBar>
  );
};

const ConnectedMenuBar = () => {
  const showingMenuBar = useSelector(selectShowingMenuBar);
  const member = useSelector(selectCurrentMember);

  /**
   * Set the active menu from route match hook POC
   *
   * This is a POC for setting the active menu directly from the useRouteMatch hook
   * Previously it was setting a state in the redux store as to which menu was currently active
   * Doing it directly from the route will be easier to maintain and has performance benefits
   *
   * TODO: Need to look at moving this to a custom hook after testing the concept
   * - being a hook it needs to be called in React function component or a custom React Hook function
   */
  const prepareMenuActiveRoutes = useRouteMatch({
    path: [
      '/maps/prepare',
      '/maps/marketplace',
      '/maps/courses',
      '/maps/insurance',
      '/maps/partner',
      '/maps/reminders',
    ],
    exact: false,
  });
  const weatherRoute = useRouteMatch({ path: '/maps/weather/', exact: false });
  const youMenuActiveRoutes = useRouteMatch({
    path: [
      '/maps/you',
      '/maps/contact-support',
      '/maps/login',
      '/maps/member',
      '/maps/menu',
      '/maps/notifications',
      '/maps/notifications/alerts',
      '/maps/settings',
      '/maps/share-feedback',
      '/maps/trips',
      '/maps/float-plans',
    ],
    exact: false,
  });

  let currentActiveMenu = MenuItemEnum.PLAN; // default
  if (prepareMenuActiveRoutes) currentActiveMenu = MenuItemEnum.PREPARE;
  else if (weatherRoute) currentActiveMenu = MenuItemEnum.WEATHER;
  else if (youMenuActiveRoutes) currentActiveMenu = MenuItemEnum.YOU;
  // /////// END - Set the active menu from route match hook POC ///////////////////

  return showingMenuBar && ConstructMenuBar(currentActiveMenu, member);
};

export default ConnectedMenuBar;
